// Setup catalog form polish — pure helpers behind the Owner-role
// multiselect and the Due-date-formula picker. The picker COMPOSES the
// legacy string grammar that LoanSetupService.ComputeDueDate already
// parses ("Close + 5 BD", "FirstPaymentDate", "UCC filing + 30 days"), so
// storage, server parsing, and seeded rows are untouched. A formula the
// picker can't express round-trips through the Advanced raw-text input.

import { addCalendarDays, addCalendarMonths } from "@/design-system/lib/format";

// ---------------------------- owner roles -----------------------------

/** Storage format is a "/"-joined code list (seed convention: "B2/B3"). */
export function splitOwnerRoleCodes(value: string): string[] {
  return value
    .split("/")
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
}

export function joinOwnerRoleCodes(codes: string[]): string {
  return codes.join("/");
}

// --------------------------- due date formula --------------------------

export type DueDateAnchor =
  | "close"
  | "firstPayment"
  | "quarterEnd"
  | "fiscalYearEnd"
  | "uccFiling";

export type DueDateOffsetUnit = "days" | "BD" | "months";

export interface DueDateFormulaModel {
  anchor: DueDateAnchor;
  /** Null = no offset (e.g. plain "FirstPaymentDate"). */
  offset: number | null;
  unit: DueDateOffsetUnit;
}

export interface DueDateAnchorOption {
  value: DueDateAnchor;
  label: string;
  /** Units the legacy grammar supports for this anchor. */
  units: DueDateOffsetUnit[];
  /** The parser requires an offset for this anchor ("First quarter-end + N days"). */
  offsetRequired?: boolean;
  /** Resolves later (UCC filing / fiscal year-end) — due date is deferred. */
  deferred?: boolean;
}

/** Anchors = exactly what LoanSetupService.ComputeDueDate parses. */
export const DUE_DATE_ANCHORS: DueDateAnchorOption[] = [
  { value: "close", label: "Closing date", units: ["days", "BD"] },
  { value: "firstPayment", label: "First payment date", units: ["days", "BD", "months"] },
  { value: "quarterEnd", label: "First quarter-end", units: ["days"], offsetRequired: true },
  { value: "fiscalYearEnd", label: "Fiscal year end", units: ["days"], deferred: true },
  { value: "uccFiling", label: "UCC filing date", units: ["days"], deferred: true },
];

export const DUE_DATE_UNIT_LABEL: Record<DueDateOffsetUnit, string> = {
  days: "calendar days",
  BD: "business days",
  months: "months",
};

/**
 * Parses a stored formula into the picker model. Regexes mirror
 * LoanSetupService.ComputeDueDate exactly; anything that doesn't match
 * (including backend-tolerated free-text suffixes after "UCC filing"/
 * "FiscalYearEnd") returns null → the modal opens in Advanced mode with
 * the raw value intact.
 */
export function parseDueDateFormula(formula: string): DueDateFormulaModel | null {
  const s = formula.trim();
  let m: RegExpMatchArray | null;

  if (/^Close$/i.test(s)) return { anchor: "close", offset: null, unit: "days" };
  if (/^FirstPaymentDate$/i.test(s))
    return { anchor: "firstPayment", offset: null, unit: "days" };
  if ((m = s.match(/^Close\s*\+\s*(\d+)\s*(days?|BD)$/i)))
    return { anchor: "close", offset: Number(m[1]), unit: unitOf(m[2]) };
  if ((m = s.match(/^FirstPaymentDate\s*\+\s*(\d+)\s*months?$/i)))
    return { anchor: "firstPayment", offset: Number(m[1]), unit: "months" };
  if ((m = s.match(/^First payment\s*\+\s*(\d+)\s*(days?|BD)$/i)))
    return { anchor: "firstPayment", offset: Number(m[1]), unit: unitOf(m[2]) };
  if ((m = s.match(/^First quarter-end\s*\+\s*(\d+)\s*days?$/i)))
    return { anchor: "quarterEnd", offset: Number(m[1]), unit: "days" };
  if (/^FiscalYearEnd$/i.test(s))
    return { anchor: "fiscalYearEnd", offset: null, unit: "days" };
  if ((m = s.match(/^FiscalYearEnd\s*\+\s*(\d+)\s*days?$/i)))
    return { anchor: "fiscalYearEnd", offset: Number(m[1]), unit: "days" };
  if (/^UCC filing$/i.test(s))
    return { anchor: "uccFiling", offset: null, unit: "days" };
  if ((m = s.match(/^UCC filing\s*\+\s*(\d+)\s*days?$/i)))
    return { anchor: "uccFiling", offset: Number(m[1]), unit: "days" };
  return null;
}

function unitOf(raw: string): DueDateOffsetUnit {
  return raw.toUpperCase() === "BD" ? "BD" : "days";
}

/** Composes the exact legacy string the server parses (and the seeds use:
 *  singular "1 day" / "1 month", "BD" uppercase). */
export function composeDueDateFormula(model: DueDateFormulaModel): string {
  const { anchor, offset, unit } = model;
  const suffix =
    offset === null
      ? ""
      : unit === "BD"
        ? ` + ${offset} BD`
        : unit === "months"
          ? ` + ${offset} month${offset === 1 ? "" : "s"}`
          : ` + ${offset} day${offset === 1 ? "" : "s"}`;

  switch (anchor) {
    case "close":
      return `Close${suffix}`;
    case "firstPayment":
      // The legacy grammar splits by unit: plain/months use the
      // "FirstPaymentDate" spelling, day offsets use "First payment".
      if (offset === null) return "FirstPaymentDate";
      if (unit === "months") return `FirstPaymentDate${suffix}`;
      return `First payment${suffix}`;
    case "quarterEnd":
      return `First quarter-end + ${offset ?? 0} days`;
    case "fiscalYearEnd":
      return `FiscalYearEnd${suffix}`;
    case "uccFiling":
      return `UCC filing${suffix}`;
  }
}

// --------------------------- deferred due dates ------------------------

/** The server stores DateTimeOffset.MinValue (year 0001) while a formula's
 *  anchor date (UCC filing / fiscal year-end) isn't recorded yet. Any
 *  pre-1970 timestamp is that sentinel — no real due date predates 1970. */
export function isDeferredDueDate(iso: string): boolean {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) || t < 0;
}

/** Neutral copy for a deferred row, e.g. "Due when the UCC filing is
 *  recorded" — never a countdown computed from the sentinel. */
export function deferredDueDateMessage(formula: string | null | undefined): string {
  const s = (formula ?? "").trim();
  if (/^UCC filing/i.test(s)) return "Due when the UCC filing is recorded";
  if (/^FiscalYearEnd/i.test(s)) return "Due when the fiscal year end is recorded";
  return "Due when its anchor date is recorded";
}

// ------------------------------ preview --------------------------------

/**
 * Computes the due date for the live "On this loan: …" preview. Only
 * first-payment anchors are computable client-side (the close date lives
 * on the loan's escrow record, not in LoanSummaryDto); deferred anchors
 * have no date by definition. BD is approximated as calendar days, same
 * as the server. Returns null when no preview is possible.
 *
 * 🔴 THE ARITHMETIC MUST MATCH `LoanSetupService.ComputeDueDate` TO THE DAY.
 * This preview is the ONLY feedback an admin gets while authoring a formula
 * that then drives the setup checklist of every loan, so a preview that
 * disagrees with the server is a wrong instruction rather than a cosmetic slip.
 * The server works in `DateOnly` and calls `.AddMonths(n)` / `.AddDays(n)`;
 * `addCalendarMonths` / `addCalendarDays` are the client twins, including
 * `AddMonths`'s day-of-month CLAMP. Do not reach for `setMonth` / `setDate`
 * (local mutators — they anchored on the previous day in every negative-offset
 * zone and produced `Mar 29` in Phoenix / `Mar 28` in New York for
 * `2026-03-01` + 1 month, where `Apr 1` is correct), and do not "simplify" the
 * helpers to `setUTCMonth`, which overflows where the server clamps.
 */
export function previewDueDate(
  model: DueDateFormulaModel,
  firstPaymentDateIso: string | null | undefined,
): Date | null {
  if (model.anchor !== "firstPayment" || !firstPaymentDateIso) return null;
  return model.offset !== null && model.unit === "months"
    ? addCalendarMonths(firstPaymentDateIso, model.offset)
    : addCalendarDays(firstPaymentDateIso, model.offset ?? 0);
}
